import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Volume2, Plus, Check, RefreshCw, Save, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playAppAudio } from "@/lib/appAudio";
import { ttsOK } from "@/lib/languages";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import Seo from "@/components/Seo";
import { LensCapture } from "@/components/LensCapture";
import { useI18n } from "@/context/I18nContext";

export default function ObjectLens() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState(null);
  const [saved, setSaved] = useState({});
  const [speaking, setSpeaking] = useState(false);

  const onCapture = async (b64) => {
    setLoading(true); setRes(null); setSaved({});
    try {
      const r = await api.post("/lens/object/identify", { image: b64 });
      setRes(r.data);
    } catch (e) {
      if (e?.response?.status === 402) toast.error("FluentLens is a premium feature.");
      else toast.error("Could not identify that. Try another photo.");
    } finally { setLoading(false); }
  };

  // Saved Vocab card audio only (shared cache + language safety); no browser speech, no language guess.
  const lang = res?.profile?.target_lang || user?.target_lang;
  const speak = async (vocabId) => {
    setSpeaking(true);
    try { const a = await playAppAudio(`/vocab/${vocabId}/audio`); a.onended = () => setSpeaking(false); a.onerror = () => setSpeaking(false); }
    catch { setSpeaking(false); toast.error("Voice unavailable right now."); }
  };

  const addVocab = async (items) => {
    const fresh = items.filter((v) => v.word && !saved[v.word]);
    if (!fresh.length) return;
    try {
      const r = await api.post("/vocab/add", { items: fresh.map((v) => ({ word: v.word, translation: v.translation, example_sentence: v.example || res?.example || "" })), source: "object_lens" });
      const ids = Object.fromEntries((r.data.items || []).map((x) => [x.word, x.id]));
      setSaved((s) => { const n = { ...s }; fresh.forEach((v) => { n[v.word] = ids[v.word] || true; }); return n; });
      toast.success(`Added ${fresh.length} to your Vocab Bank`);
    } catch { toast.error("Couldn't add to Vocab Bank."); }
  };

  const related = res?.related || [];
  const saveScan = async () => {
    try {
      await api.post("/lens/scans", { type: "object", object_label: res.target_word, saved_vocab: Object.keys(saved) });
      toast.success("Saved to FluentLens Archive");
    } catch { toast.error("Couldn't save scan."); }
  };

  return (
    <AppLayout>
      <Seo title="Object Lens · FluentLens" />
      <div className="max-w-3xl mx-auto" data-testid="object-lens">
        <Link to="/lens" className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 mb-3"><ArrowLeft className="w-4 h-4" /> FluentLens</Link>
        <h1 className="font-cabinet font-extrabold text-3xl mb-1">{t("lens.objectLens")}</h1>
        <p className="text-gray-600 text-sm mb-5">{t("lensDesc.subObject")}</p>

        {!res && <LensCapture onCapture={onCapture} loading={loading} hint={t("lensDesc.hintObject")} />}

        {res && (
          <div className="space-y-4">
            <div className="nb-card bg-peach/40 p-5" data-testid="object-result">
              <div className="text-sm text-gray-600">{res.confident ? "This is" : "This appears to be"} <b dir="auto">{res.identification_native}</b></div>
              <div className="flex items-center gap-3 mt-2">
                <div className="font-cabinet font-extrabold text-4xl" data-testid="object-word" dir="auto">{res.article ? `${res.article} ` : ""}{res.target_word}</div>
                {ttsOK(lang) && typeof saved[res.target_word] === "string" && <button onClick={() => speak(saved[res.target_word])} disabled={speaking} className="w-11 h-11 bg-white border-2 border-ink rounded-full flex items-center justify-center" data-testid="object-speak">{speaking ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Volume2 className="w-5 h-5" />}</button>}
              </div>
              <div className="text-lg font-bold text-gray-700" dir="auto">{res.translation}</div>
              {res.pronunciation && <div className="text-sm font-mono">/{res.pronunciation}/</div>}
              {res.plural && <div className="text-xs text-gray-500 mt-1" dir="auto">{t("label.plural")}: {res.plural}</div>}
              {res.example && <div className="bg-white/70 border-2 border-ink rounded-xl p-3 mt-3"><p className="font-bold" dir="auto">{res.example}</p><p className="text-sm text-gray-600" dir="auto">{res.example_translation}</p></div>}
              <div className="flex gap-2 mt-3">
                {!saved[res.target_word] ? <button onClick={() => addVocab([{ word: res.target_word, translation: res.translation, example: res.example }])} className="nb-btn-primary py-2 px-4 text-sm" data-testid="object-add-word"><Plus className="w-4 h-4" /> Add word</button>
                  : <span className="nb-tag bg-mint text-xs flex items-center gap-1"><Check className="w-3 h-3" /> Added</span>}
                {related.length > 0 && <button onClick={() => addVocab([{ word: res.target_word, translation: res.translation, example: res.example }, ...related])} className="nb-btn-outline py-2 px-4 text-sm" data-testid="object-add-related">Add related words</button>}
              </div>
            </div>

            {res.caution && (
              <div className="nb-card bg-butter/50 p-3 flex items-start gap-2 text-sm" data-testid="object-caution">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>{res.caution}</span>
              </div>
            )}

            {(related.length > 0 || (res.verbs || []).length > 0 || (res.adjectives || []).length > 0) && (
              <div className="nb-card p-4" data-testid="object-cluster">
                <div className="font-cabinet font-bold mb-2">Words around it</div>
                <div className="flex flex-wrap gap-2">
                  {related.map((v, i) => <button key={`${v.word}-${i}`} onClick={() => addVocab([v])} className={`nb-tag text-xs ${saved[v.word] ? "bg-mint" : "bg-white"}`} dir="auto">{v.word} · {v.translation}{saved[v.word] ? " ✓" : " +"}</button>)}
                </div>
                {((res.verbs || []).length > 0 || (res.adjectives || []).length > 0) && (
                  <div className="text-sm text-gray-600 mt-3 space-y-1">
                    {(res.verbs || []).length > 0 && <div><b>{t("label.verbs")}:</b> {res.verbs.map((v) => `${v.word} (${v.translation})`).join(", ")}</div>}
                    {(res.adjectives || []).length > 0 && <div><b>{t("label.adjectives")}:</b> {res.adjectives.map((v) => `${v.word} (${v.translation})`).join(", ")}</div>}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={() => { setRes(null); }} className="nb-btn-secondary flex-1 py-3" data-testid="object-again"><RefreshCw className="w-4 h-4" /> Scan again</button>
              <button onClick={saveScan} className="nb-btn-outline flex-1 py-3" data-testid="object-save-scan"><Save className="w-4 h-4" /> {t("label.save")}</button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
